import React, { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { Loading } from 'components/Loading'
import { Spinner } from 'components/Spinner'
import PageNotFound from 'routes/PageNotFound/PageNotFound'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { links, tradingAccountWelcomeDetailsRouteUrl } from 'utils/featureToggles'
import { hasPermission } from 'utils/permissionsValidator'

import { TradingAccountTab } from '../Navigation/TradingAccountNavigation'
import WelcomeAccountDetailsPage from './WelcomeAccountDetailsPage'
import WelcomeTradingAccountPendingOrdersPage from './WelcomeTradingAccountPendingOrdersPage'
import WelcomeTradingAccountPlatformTransactionDetailsPage from './WelcomeTradingAccountPlatformTransactionDetailsPage'
import WelcomeTradingAccountPositionsPage from './WelcomeTradingAccountPositionsPage'
import WelcomeTradingAccountStatisticsDetailsPage from './WelcomeTradingAccountStatisticsDetailsPage'

const WelcomeTradingAccountRoute = (): JSX.Element => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const params = useParams<{ id: string; slug: TradingAccountTab }>()
  const { id, slug } = params

  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const [crumbs, setCrumbs] = useState([
    { title: 'Trading Accounts - Welcome', link: links.recordsWelcomeAccounts.url },
  ])

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(() => {
    return id ? tradingAccountsApiClient.getWelcomeAccount(id) : Promise.resolve(null)
  }, [id])
  const { data: tradingAccount, isLoading } = useFetchOne(fetchData)

  useEffect(() => {
    if (tradingAccount) {
      const [crumb1, crumb2] = [
        ...crumbs,
        {
          title: tradingAccount.name,
          link: tradingAccountWelcomeDetailsRouteUrl(
            tradingAccount,
            slug
              ? matchesSlug(slug)
                ? slug
                : TradingAccountTab.Details
              : TradingAccountTab.Details
          ),
        },
      ]
      setCrumbs([crumb1, crumb2])
    }
  }, [tradingAccount]) // eslint-disable-line react-hooks/exhaustive-deps

  const matchesSlug = (tradingAccountDetailsSlug?: TradingAccountTab) => {
    if (!tradingAccountDetailsSlug) {
      return false
    }
    return (
      tradingAccountDetailsSlug === TradingAccountTab.Details ||
      tradingAccountDetailsSlug === TradingAccountTab.Statistics ||
      tradingAccountDetailsSlug === TradingAccountTab.Orders ||
      tradingAccountDetailsSlug === TradingAccountTab.Positions ||
      tradingAccountDetailsSlug === TradingAccountTab.PlatformTransactions
    )
  }

  const innerRender = () => {
    if (!matchesSlug(slug)) {
      return <PageNotFound />
    }
    if (isLoading) {
      return <Spinner />
    }
    if (!tradingAccount) {
      return <PageNotFound />
    }
    if (
      slug === TradingAccountTab.Details &&
      hasPermission(permissions, PermissionsEnum.CanViewTradingAccount)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <WelcomeAccountDetailsPage data={tradingAccount} forceRefresh={forceRefresh} />
        </React.Fragment>
      )
    }
    if (
      slug === TradingAccountTab.Statistics &&
      hasPermission(permissions, PermissionsEnum.CanViewTradingAccountStatistics)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <WelcomeTradingAccountStatisticsDetailsPage data={tradingAccount} />
        </React.Fragment>
      )
    }
    if (
      slug === TradingAccountTab.Positions &&
      hasPermission(permissions, PermissionsEnum.CanViewTradingAccountOpenPositions)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <WelcomeTradingAccountPositionsPage data={tradingAccount} />
        </React.Fragment>
      )
    }
    if (
      slug === TradingAccountTab.Orders &&
      hasPermission(permissions, PermissionsEnum.CanViewTradingAccountPendingOrders)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <WelcomeTradingAccountPendingOrdersPage data={tradingAccount} />
        </React.Fragment>
      )
    }
    if (
      slug === TradingAccountTab.PlatformTransactions &&
      hasPermission(permissions, PermissionsEnum.CanViewTradingAccountPlatformTransactions)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <WelcomeTradingAccountPlatformTransactionDetailsPage data={tradingAccount} />
        </React.Fragment>
      )
    }
    return <PageNotFound />
  }

  return <Loading isLoading={isLoading && !matchesSlug(slug)}>{innerRender()}</Loading>
}

export default WelcomeTradingAccountRoute
